import { ActivityFormContext } from '@/contexts/ActivityFormContext';
import { SmartToy } from '@mui/icons-material';
import { Box, Fade, Popper, Stack, Typography, useTheme } from '@mui/material';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type AssistantSuggestionPopperProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  loading: boolean;
  onClose?: () => void;
  onAccept?: () => void;
  placement?: 'bottom-start' | 'bottom-end' | 'bottom';
  arrowAlign?: 'left' | 'right' | 'center';
  activateAi?: boolean;
  sx?: React.CSSProperties;
  children: React.ReactNode;
};

const AssistantSuggestionPopper = (props: AssistantSuggestionPopperProps) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const formHandler = useContext(ActivityFormContext);
  const id = props.open ? 'assistant-suggestion-popper' : undefined;

  useEffect(() => {
    if (!props.open && !props.loading) {
      formHandler.setShowAssistantBadge?.(true);
    }
  }, [props.loading]);

  return (
    <Popper
      id={id}
      open={Boolean(props.anchorEl)}
      anchorEl={props.anchorEl}
      placement={props.placement ?? 'bottom-end'}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ]}
      sx={{ zIndex: 2000 }}
    >
      <Box sx={{ position: 'relative' }}>
        <Fade in>
          <Box
            sx={{
              boxSizing: 'border-box',
              backgroundColor:
                palette.mode === 'dark' ? palette.primary.dark : palette.primary.light,
              padding: 2,
              borderRadius: 2,
              ml: 2,
              minWidth: 'min(250px, 80vw)',
              maxWidth: 'min(500px, 90vw)',
              width: 'fit-content',
              boxShadow: 3,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-8px',
                right:
                  props.arrowAlign === 'left'
                    ? 'auto'
                    : props.arrowAlign === 'center'
                    ? 'calc(50% - 8px)'
                    : '16px',
                left: props.arrowAlign === 'left' ? '16px' : 'auto',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: `8px solid ${
                  palette.mode === 'dark' ? palette.primary.dark : palette.primary.light
                }`,
              },
              ...props.sx,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <SmartToy fontSize="large" sx={{ color: palette.grey[50] }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }} color={palette.grey[50]}>
                {t('AI Assistant')}
              </Typography>
            </Stack>

            {props.children}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1.5 }}>
              {props.activateAi && (
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', cursor: 'pointer', color: palette.grey[50] }}
                  onClick={props.onAccept}
                >
                  {t('Activate AI')}
                </Typography>
              )}
            </Box>
          </Box>
        </Fade>
      </Box>
    </Popper>
  );
};

export default AssistantSuggestionPopper;
